import * as mutationType from "../mutation_types";
import { formatPaginationParams, TOTAL_COUNT_KEY } from "../../helpers/api";
import _ from "lodash";
import moment from "moment";

const state = {
  report: {},
  reports: [],
  text_responses: {},
  answer_users: [],
  instances: [],
  participation: {},
  trend: [],
  heatmap: {},
  heatmap_detail: {},
  goal: {},
  goal_trends: [],
  team_goals: [],
  usage: [],
  usage_trends: [],
  pulse: {},
  multiple_trends: [],
  overall_score: {},
  review: {},
  recognition: {},
  manager_score: {},
  return_url: "",
  timeframe: {},
  teams: [],
  users: [],
  trend_types: [],
  business_metrics_trend: [],
  business_metric_id: null,
  sortBy: null,
  sortDesc: false
};

const getters = {
  reports: state => state.reports || [],
  primary: state => state.report.primary || [],
  compare: state => state.report.compare || [],
  survey_questions: state => state.report.questions || [],
  survey: state => state.report.survey || {},
  overview: state => state.report.overview || { userRecords: {} },
  text_responses: state => questionId => state.text_responses[questionId],
  answer_users: state => state.answer_users || [],
  sent_surveys: state => state.report.sentSurveys || [],
  sent_resources: state => state.report.sentResources || [],
  sent_resources_users: state => state.report.sentResourceUsers || [],
  instances: state => state.instances,
  participation: state => state.participation,
  trend: state => state.trend,
  heatmap: state => state.heatmap,
  heatmap_detail: state => state.heatmap_detail,
  goal: state => state.goal,
  goal_trends: state => state.goal_trends,
  team_goals: state => state.team_goals,
  usage: state => state.usage,
  usage_trends: state => state.usage_trends,
  pulse: state => state.pulse,
  overall_score: state => state.overall_score,
  review: state => state.review,
  recognition: state => state.recognition,
  manager_score: state => state.manager_score,
  return_url: state => state.return_url,
  timeframe: state => state.timeframe,
  teams: state => state.teams,
  users: state => state.users,
  trend_types: state => state.trend_types,
  business_metrics_trend: state => state.business_metrics_trend,
  business_metric_id: state => state.business_metric_id,
  business_metric: (state, getters, rootState, rootGetters) =>
    rootGetters["company/business_metrics"].find(
      x => x.id == state.business_metric_id
    ) || {},
  multiple_trends: state => state.multiple_trends,
  sortBy: state => state.sortBy,
  sortDesc: state => state.sortDesc || false
};

const actions = {
  getReports({ commit, state }) {
    return new Promise((resolve, reject) => {
      if (state.reports && state.reports.length > 0) {
        resolve();
      } else {
        this._vm.$http
          .get("/api/reporting")
          .then(res => {
            commit(mutationType.SET_REPORTING_LIST, res.data);
            resolve();
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      }
    });
  },
  setReturnUrl({ commit }, url) {
    return new Promise(resolve => {
      commit(mutationType.SET_REPORTING_RETURN_URL, url);
      resolve();
    });
  },
  setTimeframe({ commit }, filter) {
    return new Promise(resolve => {
      commit(mutationType.SET_REPORTING_TIMEFRAME, filter);
      resolve();
    });
  },
  getSurveyReport({ commit }, request) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post("/api/reporting/survey/" + request.surveyId, request.filter || {})
        .then(res => {
          commit(mutationType.SET_REPORTING_SURVEY_REPORT, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getSurveyTextResponses({ commit }, request) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post(
          "/api/reporting/survey/" +
            request.surveyId +
            "/questions/" +
            request.questionId +
            "/responses",
          request.filter,
          { params: formatPaginationParams(request.pagination) }
        )
        .then(res => {
          commit(mutationType.SET_REPORTING_SURVEY_TEXT_RESPONSES, {
            questionId: request.questionId,
            responses: res.data
          });
          resolve(res.headers[TOTAL_COUNT_KEY]);
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getSurveyAnswerUsers({ commit }, request) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post(
          `/api/reporting/survey/${request.surveyId}/questions/${request.questionId}/answers/${request.answerId}/users`,
          request.filter
        )
        .then(res => {
          commit(mutationType.SET_REPORTING_SURVEY_ANSWER_USERS, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getSurveysSentReport({ commit }, filter) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post("/api/reporting/surveys", filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_SURVEYS, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getResourcesReport({ commit }, filter) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post("/api/reporting/resources", filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_RESOURCES, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getResourceReport({ commit }, filter) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post(`/api/reporting/resources/${filter.resourceId}`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_RESOURCES, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getInstances({ commit }, filter) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post(
          filter.surveyId
            ? `/api/reporting/survey/${filter.surveyId}/instances`
            : `/api/reporting/resources/${filter.resourceId}/schedules`,
          filter
        )
        .then(res => {
          commit(mutationType.SET_REPORTING_INSTANCES, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getPulseCompanyHeatmap({ commit }, filter) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post(`/api/reporting/pulse/companyheatmap`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_HEATMAP, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getPulseHeatmap({ commit }, filter) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post(`/api/reporting/pulse/heatmap`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_HEATMAP, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getPulseCompanyHeatmapDetail({ commit }, filter) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post(`/api/reporting/pulse/companyheatmap/detail`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_HEATMAP_DETAIL, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getPulseHeatmapDetail({ commit }, filter) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post(`/api/reporting/pulse/heatmap/detail`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_HEATMAP_DETAIL, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  // eslint-disable-next-line no-empty-pattern
  exportPulseCompanyHeatmap({}, filter) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post("/api/reporting/pulse/companyheatmap/export", filter)
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `heatmap_export_${moment().format("MM_DD_YYYY_HH_mm")}.csv`
          );
          document.body.appendChild(link);
          link.click();
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  // eslint-disable-next-line no-empty-pattern
  exportPulseHeatmap({}, filter) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post("/api/reporting/pulse/heatmap/export", filter)
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `heatmap_export_${moment().format("MM_DD_YYYY_HH_mm")}.csv`
          );
          document.body.appendChild(link);
          link.click();
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getReviewParticipationTrend({ commit }) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .get(`/api/reporting/review/participation/trend`)
        .then(res => {
          commit(mutationType.SET_REPORTING_TREND, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getReviewHeatmap({ commit }, filter) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post(`/api/reporting/review/heatmap`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_HEATMAP, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getGoalReport({ commit }, filter) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post(`/api/reporting/goals`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_GOALS, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  clearGoalReport({ commit }) {
    return new Promise(resolve => {
      commit(mutationType.SET_REPORTING_GOALS, {});
      resolve();
    });
  },
  getGoalReportTeams({ commit, state }, filter) {
    return new Promise((resolve, reject) => {
      if (state.goal.teams && state.goal.teams.length > 0) {
        resolve();
        return;
      }
      this._vm.$http
        .post(`/api/reporting/goals/teams`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_GOALS, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getGoalReportManagers({ commit }, filter) {
    return new Promise((resolve, reject) => {
      if (state.goal.managers && state.goal.managers.length > 0) {
        resolve();
        return;
      }
      this._vm.$http
        .post(`/api/reporting/goals/managers`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_GOALS, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getGoalReportMembers({ commit }, filter) {
    return new Promise((resolve, reject) => {
      if (state.goal.members && state.goal.members.length > 0) {
        resolve();
        return;
      }
      this._vm.$http
        .post(`/api/reporting/goals/members`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_GOALS, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getGoalReportTrend({ commit }, filter) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post(`/api/reporting/goals/trend`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_GOALS_TRENDS, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  clearParticipation({ commit }) {
    return new Promise(resolve => {
      commit(mutationType.SET_REPORTING_PARTICIPATION, {});
      resolve();
    });
  },
  getParticipationTeams({ commit, state }, filter) {
    return new Promise((resolve, reject) => {
      if (state.participation.teams && state.participation.teams.length > 0) {
        resolve();
        return;
      }
      this._vm.$http
        .post(`/api/reporting/participation/teams`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_PARTICIPATION, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getParticipationManagers({ commit }, filter) {
    return new Promise((resolve, reject) => {
      if (
        state.participation.managers &&
        state.participation.managers.length > 0
      ) {
        resolve();
        return;
      }
      this._vm.$http
        .post(`/api/reporting/participation/managers`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_PARTICIPATION, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getParticipationMembers({ commit }, filter) {
    return new Promise((resolve, reject) => {
      if (
        state.participation.members &&
        state.participation.members.length > 0
      ) {
        resolve();
        return;
      }
      this._vm.$http
        .post(`/api/reporting/participation/members`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_PARTICIPATION, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getParticipationMember({ commit }, filter) {
    return new Promise((resolve, reject) => {
      if (state.participation.member && state.participation.member.length > 0) {
        resolve();
        return;
      }
      this._vm.$http
        .post(`/api/reporting/participation/member`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_PARTICIPATION, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getParticipation({ commit }, filter) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post(`/api/reporting/participation`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_PARTICIPATION, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getParticipationTrend({ commit }, filter) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post(`/api/reporting/participation/trend`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_TREND, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getUsage({ commit }, filter) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post(`/api/reporting/usage`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_USAGE, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getUsageTrends({ commit }, filter) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post(`/api/reporting/usage/trends`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_USAGE_TRENDS, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  clearPulse({ commit }) {
    return new Promise(resolve => {
      commit(mutationType.SET_REPORTING_PULSE, {});
      resolve();
    });
  },
  getPulse({ commit }, filter) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post(`/api/reporting/pulse`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_PULSE, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getPulseTeams({ commit, state }, filter) {
    return new Promise((resolve, reject) => {
      if (state.pulse.teams && state.pulse.teams.length > 0) {
        resolve();
        return;
      }
      this._vm.$http
        .post(`/api/reporting/pulse/teams`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_PULSE, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getPulseManagers({ commit }, filter) {
    return new Promise((resolve, reject) => {
      if (state.pulse.managers && state.pulse.managers.length > 0) {
        resolve();
        return;
      }
      this._vm.$http
        .post(`/api/reporting/pulse/managers`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_PULSE, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getPulseMembers({ commit }, filter) {
    return new Promise((resolve, reject) => {
      if (state.pulse.members && state.pulse.members.length > 0) {
        resolve();
        return;
      }
      this._vm.$http
        .post(`/api/reporting/pulse/members`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_PULSE, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getPulseTrend({ commit }, filter) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post(`/api/reporting/pulse/trend`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_MULTIPLE_TRENDS, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getEnpsTrend({ commit }, filter) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post(`/api/reporting/enps/trend`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_TREND, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getOverallScore({ commit }, filter) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post(`/api/reporting/overallscore`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_OVERALL_SCORE, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  clearOverallScore({ commit }) {
    return new Promise(resolve => {
      commit(mutationType.SET_REPORTING_OVERALL_SCORE, {});
      resolve();
    });
  },
  getOverallScoreTeams({ commit, state }, filter) {
    return new Promise((resolve, reject) => {
      if (state.overall_score.teams && state.overall_score.teams.length > 0) {
        resolve();
        return;
      }
      this._vm.$http
        .post(`/api/reporting/overallscore/teams`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_OVERALL_SCORE, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getOverallScoreManagers({ commit }, filter) {
    return new Promise((resolve, reject) => {
      if (
        state.overall_score.managers &&
        state.overall_score.managers.length > 0
      ) {
        resolve();
        return;
      }
      this._vm.$http
        .post(`/api/reporting/overallscore/managers`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_OVERALL_SCORE, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getOverallScoreMembers({ commit }, filter) {
    return new Promise((resolve, reject) => {
      if (
        state.overall_score.members &&
        state.overall_score.members.length > 0
      ) {
        resolve();
        return;
      }
      this._vm.$http
        .post(`/api/reporting/overallscore/members`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_OVERALL_SCORE, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getOverallScoreTrend({ commit }, filter) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post(`/api/reporting/overallscore/trend`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_TREND, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getReview({ commit }, filter) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post(`/api/reporting/review`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_REVIEW, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  clearReview({ commit }) {
    return new Promise(resolve => {
      commit(mutationType.SET_REPORTING_REVIEW, {});
      resolve();
    });
  },
  getReviewTeams({ commit, state }, filter) {
    return new Promise((resolve, reject) => {
      if (state.review.teams && state.review.teams.length > 0) {
        resolve();
        return;
      }
      this._vm.$http
        .post(`/api/reporting/review/teams`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_REVIEW, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getReviewManagers({ commit }, filter) {
    return new Promise((resolve, reject) => {
      if (state.review.managers && state.review.managers.length > 0) {
        resolve();
        return;
      }
      this._vm.$http
        .post(`/api/reporting/review/managers`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_REVIEW, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getReviewMembers({ commit }, filter) {
    return new Promise((resolve, reject) => {
      if (state.review.members && state.review.members.length > 0) {
        resolve();
        return;
      }
      this._vm.$http
        .post(`/api/reporting/review/members`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_REVIEW, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getReviewCategories({ commit }, filter) {
    return new Promise((resolve, reject) => {
      if (state.review.categories && state.review.categories.length > 0) {
        resolve();
        return;
      }
      this._vm.$http
        .post(`/api/reporting/review/member/categories`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_REVIEW, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getReviewQuestions({ commit }, filter) {
    return new Promise((resolve, reject) => {
      if (state.review.questions && state.review.questions.length > 0) {
        resolve();
        return;
      }
      this._vm.$http
        .post(`/api/reporting/review/member/questions`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_REVIEW, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getReviews({ commit }, filter) {
    return new Promise((resolve, reject) => {
      if (state.review.reviews && state.review.reviews.length > 0) {
        resolve();
        return;
      }
      this._vm.$http
        .post(`/api/reporting/review/member/reviews`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_REVIEW, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getReviewTrend({ commit }, filter) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post(`/api/reporting/review/trend`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_MULTIPLE_TRENDS, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getRecognition({ commit }, filter) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post(`/api/reporting/recognition`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_RECOGNITION, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  clearRecognition({ commit }) {
    return new Promise(resolve => {
      commit(mutationType.SET_REPORTING_RECOGNITION, {});
      resolve();
    });
  },
  getRecognitionTeams({ commit, state }, filter) {
    return new Promise((resolve, reject) => {
      if (state.recognition.teams && state.recognition.teams.length > 0) {
        resolve();
        return;
      }
      this._vm.$http
        .post(`/api/reporting/recognition/teams`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_RECOGNITION, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getRecognitionManagers({ commit }, filter) {
    return new Promise((resolve, reject) => {
      if (state.recognition.managers && state.recognition.managers.length > 0) {
        resolve();
        return;
      }
      this._vm.$http
        .post(`/api/reporting/recognition/managers`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_RECOGNITION, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getRecognitionMembers({ commit }, filter) {
    return new Promise((resolve, reject) => {
      if (state.recognition.members && state.recognition.members.length > 0) {
        resolve();
        return;
      }
      this._vm.$http
        .post(`/api/reporting/recognition/members`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_RECOGNITION, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getRecognitionMember({ commit }, filter) {
    return new Promise((resolve, reject) => {
      if (state.recognition.member && state.recognition.member.length > 0) {
        resolve();
        return;
      }
      this._vm.$http
        .post(`/api/reporting/recognition/member`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_RECOGNITION, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getRecognitionTrend({ commit }, filter) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post(`/api/reporting/recognition/trend`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_TREND, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getManagerScore({ commit }, filter) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post(`/api/reporting/managerscore`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_MANAGER_SCORE, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  clearManagerScore({ commit }) {
    return new Promise(resolve => {
      commit(mutationType.SET_REPORTING_MANAGER_SCORE, {});
      resolve();
    });
  },
  getManagerScoreTeams({ commit, state }, filter) {
    return new Promise((resolve, reject) => {
      if (state.manager_score.teams && state.manager_score.teams.length > 0) {
        resolve();
        return;
      }
      this._vm.$http
        .post(`/api/reporting/managerscore/teams`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_MANAGER_SCORE, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getManagerScoreManagers({ commit }, filter) {
    return new Promise((resolve, reject) => {
      if (
        state.manager_score.managers &&
        state.manager_score.managers.length > 0
      ) {
        resolve();
        return;
      }
      this._vm.$http
        .post(`/api/reporting/managerscore/managers`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_MANAGER_SCORE, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getManagerScoreMembers({ commit }, filter) {
    return new Promise((resolve, reject) => {
      if (
        state.manager_score.members &&
        state.manager_score.members.length > 0
      ) {
        resolve();
        return;
      }
      this._vm.$http
        .post(`/api/reporting/managerscore/members`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_MANAGER_SCORE, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getManagerScoreTrend({ commit }, filter) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post(`/api/reporting/managerscore/trend`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_TREND, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getTeams({ commit }) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .get("/api/reporting/teams")
        .then(res => {
          commit(mutationType.SET_REPORTING_TEAMS, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getUsers({ commit }) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .get("/api/reporting/users")
        .then(res => {
          commit(mutationType.SET_REPORTING_USERS, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getTrendTypes({ commit }) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .get("/api/reporting/trends")
        .then(res => {
          commit(mutationType.SET_REPORTING_TREND_TYPES, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  getBusinessMetricTrend({ commit }, filter) {
    if (!filter.businessMetricId) {
      commit(mutationType.SET_REPORTING_BUSINESS_METRICS_TREND, []);
      return;
    }
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post(`/api/reporting/businessmetrics/trend`, filter)
        .then(res => {
          commit(mutationType.SET_REPORTING_BUSINESS_METRICS_TREND, res.data);
          resolve();
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  },
  setBusinessMetricId({ commit }, id) {
    return new Promise(resolve => {
      commit(mutationType.SET_REPORTING_BUSINESS_METRIC_ID, id);
      resolve();
    });
  },
  setSortBy({ commit }, val) {
    return new Promise(resolve => {
      commit(mutationType.SET_REPORTING_SORT_BY, val);
      resolve();
    });
  },
  setSortDesc({ commit }, val) {
    return new Promise(resolve => {
      commit(mutationType.SET_REPORTING_SORT_DESC, val);
      resolve();
    });
  }
};

const mutations = {
  [mutationType.SET_REPORTING_LIST](state, list) {
    state.reports = list;
  },
  [mutationType.SET_REPORTING_RETURN_URL](state, url) {
    state.return_url = url;
  },
  [mutationType.SET_REPORTING_TIMEFRAME](state, item) {
    state.timeframe = item;
  },
  [mutationType.SET_REPORTING_SURVEY_REPORT](state, report) {
    state.report = report;
  },
  [mutationType.SET_REPORTING_SURVEY_TEXT_RESPONSES](state, data) {
    this._vm.$set(state.text_responses, data.questionId, data.responses);
  },
  [mutationType.SET_REPORTING_SURVEY_ANSWER_USERS](state, list) {
    state.answer_users = list;
  },
  [mutationType.SET_REPORTING_RESOURCES](state, report) {
    state.report = report;
  },
  [mutationType.SET_REPORTING_INSTANCES](state, list) {
    state.instances = list;
  },
  [mutationType.SET_REPORTING_SURVEYS](state, report) {
    state.report = report;
  },
  [mutationType.SET_REPORTING_PARTICIPATION](state, item) {
    if (_.isEmpty(item)) {
      state.participation = {};
    } else {
      if (item.teams) {
        this._vm.$set(state.participation, "teams", item.teams);
      }
      if (item.managers) {
        this._vm.$set(state.participation, "managers", item.managers);
      }
      if (item.members) {
        this._vm.$set(state.participation, "members", item.members);
      }
      if (item.member) {
        this._vm.$set(state.participation, "member", item.member);
      }
      if (item.overview) {
        this._vm.$set(state.participation, "overview", item.overview);
      }
    }
  },
  [mutationType.SET_REPORTING_TREND](state, list) {
    state.trend = list;
  },
  [mutationType.SET_REPORTING_HEATMAP](state, heatmap) {
    state.heatmap = heatmap;
  },
  [mutationType.SET_REPORTING_HEATMAP_DETAIL](state, item) {
    state.heatmap_detail = item;
  },
  [mutationType.SET_REPORTING_GOALS](state, item) {
    if (_.isEmpty(item)) {
      state.goal = {};
    } else {
      if (item.teams) {
        this._vm.$set(state.goal, "teams", item.teams);
      }
      if (item.managers) {
        this._vm.$set(state.goal, "managers", item.managers);
      }
      if (item.members) {
        this._vm.$set(state.goal, "members", item.members);
      }
      if (item.statusItems) {
        this._vm.$set(state.goal, "statusItems", item.statusItems);
      }
      if (item.overview) {
        this._vm.$set(state.goal, "overview", item.overview);
      }
    }
  },
  [mutationType.SET_REPORTING_GOALS_TRENDS](state, list) {
    state.goal_trends = list;
  },
  [mutationType.SET_REPORTING_TEAM_GOALS](state, goals) {
    state.team_goals = goals;
  },
  [mutationType.SET_REPORTING_USAGE](state, list) {
    state.usage = list;
  },
  [mutationType.SET_REPORTING_USAGE_TRENDS](state, list) {
    state.usage_trends = list;
  },
  [mutationType.SET_REPORTING_PULSE](state, item) {
    if (_.isEmpty(item)) {
      state.pulse = {};
    } else {
      if (item.teams) {
        this._vm.$set(state.pulse, "teams", item.teams);
      }
      if (item.managers) {
        this._vm.$set(state.pulse, "managers", item.managers);
      }
      if (item.members) {
        this._vm.$set(state.pulse, "members", item.members);
      }
      if (item.overview) {
        this._vm.$set(state.pulse, "overview", item.overview);
      }
    }
  },
  [mutationType.SET_REPORTING_MULTIPLE_TRENDS](state, list) {
    state.multiple_trends = list;
  },
  [mutationType.SET_REPORTING_OVERALL_SCORE](state, item) {
    if (_.isEmpty(item)) {
      state.overall_score = {};
    } else {
      if (item.teams) {
        this._vm.$set(state.overall_score, "teams", item.teams);
      }
      if (item.managers) {
        this._vm.$set(state.overall_score, "managers", item.managers);
      }
      if (item.members) {
        this._vm.$set(state.overall_score, "members", item.members);
      }
      if (item.overview) {
        this._vm.$set(state.overall_score, "overview", item.overview);
      }
    }
  },
  [mutationType.SET_REPORTING_REVIEW](state, item) {
    if (_.isEmpty(item)) {
      state.review = {};
    } else {
      if (item.teams) {
        this._vm.$set(state.review, "teams", item.teams);
      }
      if (item.managers) {
        this._vm.$set(state.review, "managers", item.managers);
      }
      if (item.members) {
        this._vm.$set(state.review, "members", item.members);
      }
      if (item.overview) {
        this._vm.$set(state.review, "overview", item.overview);
      }
      if (item.categories) {
        this._vm.$set(state.review, "categories", item.categories);
      }
      if (item.questions) {
        this._vm.$set(state.review, "questions", item.questions);
      }
      if (item.reviews) {
        this._vm.$set(state.review, "reviews", item.reviews);
      }
    }
  },
  [mutationType.SET_REPORTING_RECOGNITION](state, item) {
    if (_.isEmpty(item)) {
      state.recognition = {};
    } else {
      if (item.teams) {
        this._vm.$set(state.recognition, "teams", item.teams);
      }
      if (item.managers) {
        this._vm.$set(state.recognition, "managers", item.managers);
      }
      if (item.members) {
        this._vm.$set(state.recognition, "members", item.members);
      }
      if (item.member) {
        this._vm.$set(state.recognition, "member", item.member);
      }
      if (item.coreValues) {
        this._vm.$set(state.recognition, "coreValues", item.coreValues);
      }
      if (item.recognitions) {
        this._vm.$set(state.recognition, "recognitions", item.recognitions);
      }
      if (item.overview) {
        this._vm.$set(state.recognition, "overview", item.overview);
      }
    }
  },
  [mutationType.SET_REPORTING_MANAGER_SCORE](state, item) {
    if (_.isEmpty(item)) {
      state.manager_score = {};
    } else {
      if (item.teams) {
        this._vm.$set(state.manager_score, "teams", item.teams);
      }
      if (item.managers) {
        this._vm.$set(state.manager_score, "managers", item.managers);
      }
      if (item.members) {
        this._vm.$set(state.manager_score, "members", item.members);
      }
      if (item.overview) {
        this._vm.$set(state.manager_score, "overview", item.overview);
      }
    }
  },
  [mutationType.SET_REPORTING_TEAMS](state, list) {
    state.teams = list;
  },
  [mutationType.SET_REPORTING_USERS](state, list) {
    state.users = list;
  },
  [mutationType.SET_REPORTING_TREND_TYPES](state, list) {
    state.trend_types = list;
  },
  [mutationType.SET_REPORTING_BUSINESS_METRICS_TREND](state, list) {
    state.business_metrics_trend = list;
  },
  [mutationType.SET_REPORTING_BUSINESS_METRIC_ID](state, id) {
    state.business_metric_id = id;
  },
  [mutationType.SET_REPORTING_SORT_BY](state, val) {
    state.sortBy = val;
  },
  [mutationType.SET_REPORTING_SORT_DESC](state, val) {
    state.sortDesc = val;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
